import { scrubLaunchParams } from "@commerce/shopper/scrub";
import { createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";

export function getRouter() {
	const router = createRouter({
		routeTree,
		defaultPreload: "intent",
		defaultPreloadStaleTime: 0,
		scrollRestoration: true,
		defaultPendingMs: 150,
		defaultPendingMinMs: 100,
		// Direction-aware page transitions. Forward navigation slides left,
		// back navigation slides right. The actual keyframes live in
		// theme.css under "VIEW TRANSITIONS"; this only signals the type.
		// See DESIGN.md § Motion.
		defaultViewTransition: {
			types: ({ fromLocation, toLocation }) => {
				const from = fromLocation?.state.__TSR_index;
				const to = toLocation.state.__TSR_index;
				if (from == null || from === to) {
					return [];
				}
				return from > to ? ["slide-right"] : ["slide-left"];
			},
		},
	});

	// Sentry on the client, on deployed environments only (not localhost). Loaded after the
	// router exists, never on the way to the first render: it is a third of the entry.
	if (
		!router.isServer &&
		typeof window !== "undefined" &&
		import.meta.env.VITE_SENTRY_DSN &&
		!["localhost", "127.0.0.1"].includes(window.location.hostname)
	) {
		void import("@sentry/tanstackstart-react").then((Sentry) => {
			Sentry.init({
				dsn: import.meta.env.VITE_SENTRY_DSN,
				environment: import.meta.env.MODE,

				// Adds request headers and IP for users
				sendDefaultPii: true,

				integrations: [
					// Browser tracing for Web Vitals (LCP, CLS, FCP, TTFB, INP) and navigation
					Sentry.tanstackRouterBrowserTracingIntegration(router),
					// Session Replay for visual debugging of user sessions and errors
					Sentry.replayIntegration(),
					// Browser Profiling for function-level performance analysis
					// Helps identify jank, frame drops, and slow functions
					Sentry.browserProfilingIntegration(),
				],

				// Performance monitoring - 20% of transactions
				tracesSampleRate: 0.2,

				// Launch tokens and codes in /c/<org> URLs never reach Sentry.
				beforeSend: scrubLaunchParams,
				beforeSendTransaction: scrubLaunchParams,

				// Third-party widgets, embedded WebViews, and browser extensions
				// throw exceptions we cannot fix from our code. Drop them so real
				// issues aren't buried.
				ignoreErrors: [
					// Cross-origin scripts stripped by the browser; no stack,
					// no signal.
					"Script error.",
					// Android WebView: the native-bridge Java object is collected
					// while JS still holds a reference (app backgrounded).
					// Lifecycle, not a bug.
					"Java object is gone",
					// Cloudflare Turnstile token expiry — the widget self-resets.
					/Turnstile.*600010/i,
					// Stale-chunk errors after deploy, auto-recovered by the
					// `vite:preloadError` reload handler in __root.tsx.
					"Importing a module script failed",
					"Failed to fetch dynamically imported module",
					"not a valid JavaScript MIME type",
					// Browser translation extensions (Google Translate, etc.)
					// replace text nodes out from under React's reconciler.
					/insertBefore.*not a child of this node/i,
					/Cannot read properties of null \(reading 'removeChild'\)/i,
				],
				// Analytics/tag-manager CDNs throw inside their own bundles.
				denyUrls: [
					/googletagmanager\.com/,
					/google-analytics\.com/,
					/\/gtag\/js/,
				],

				// Continuous profiling for detailed performance insights (Chromium browsers only)
				profileSessionSampleRate: 0.1,

				// Session replay rates
				replaysSessionSampleRate: 0.1,
				replaysOnErrorSampleRate: 1.0,
			});
		});
	}

	return router;
}

declare module "@tanstack/react-router" {
	interface Register {
		router: ReturnType<typeof getRouter>;
	}
}
